// src/features/boe/components/programs/Program0300.jsx

import React from "react";
import { ProgramBudget } from "../../../../budget/model/program/ProgramBudget.jsx";

/**
 * Configuration object defining how Program 0300 (Basic Education)
 * maps Firestore budget data into editable, year-based budget tables.
 *
 * The <ProgramBudget /> engine uses this config to produce:
 * - Approved & Actual budget tables
 * - UI field groupings (positions, salaries, services, materials)
 * - Value extraction rules from nested Firestore blocks
 *
 * @typedef {Object} ProgramFieldDefinition
 * @property {string} key - Unique internal identifier used for rendering & state management.
 * @property {string} label - Human-readable description for the UI.
 * @property {"currency"|"count"|"text"} valueType - Determines formatting (currency, FTE count, etc.).
 * @property {Array<{block: string, key: string}>} sources - One or more Firestore budget data sources.
 * @property {"positions"|"salaries"|"services"|"materials"} group - Logical section grouping in UI.
 * @property {boolean} defaultVisible - Whether the field is visible by default in tables.
 *
 * @typedef {Object} ProgramTableDefinition
 * @property {string} id - Unique table ID ("approvedMain", "actualMain").
 * @property {string} title - Table title shown in the UI header.
 * @property {string} sectionKey - Firestore block key ("Approved" or "Actual").
 * @property {string[]} fields - Ordered list of field keys to include in the table.
 * @property {Object<string, Object>} columnStyles - Optional CSS width constraints.
 *
 * @typedef {Object} ProgramConfig
 * @property {string} fullName - Program display name.
 * @property {string} docId - Firestore document identifier for this program.
 * @property {ProgramFieldDefinition[]} fieldDefs - All field definitions belonging to this program.
 * @property {ProgramTableDefinition[]} tables - Approved/Actual table definitions.
 * @property {?string[]} inflationSumFields - Optional fields used for inflation aggregation.
 */

/**
 * @type {ProgramConfig}
 */
export const PROGRAM_0300_CONFIG = {
  fullName: "BASIC EDUCATION",
  docId: "0300",

  /**
   * Field definitions describing how to access, label, and display
   * Basic Education budget data from nested Firestore structures.
   */
  fieldDefs: [
    // -----------------------------------------------------------
    // POSITIONS (FTE)
    // -----------------------------------------------------------
    {
      key: "positionsAdmin",
      label: "Positions – Administrative",
      valueType: "count",
      sources: [{ block: "Positions", key: "Administrative" }],
      group: "positions",
      defaultVisible: false,
    },
    {
      key: "positionsTeachers",
      label: "Positions – Teachers",
      valueType: "count",
      sources: [{ block: "Positions", key: "Teachers" }],
      group: "positions",
      defaultVisible: false,
    },
    {
      key: "positionsTeachersTitleIGrant",
      label: "Title I Grant Teachers",
      valueType: "count",
      sources: [{ block: "Positions", key: "Teachers - Title I Grant" }],
      group: "positions",
      defaultVisible: false,
    },

    // -----------------------------------------------------------
    // SALARIES
    // -----------------------------------------------------------
    {
      key: "salariesAdmin",
      label: "Salaries – Administrative",
      valueType: "currency",
      sources: [{ block: "Salaries", key: "Administrative" }],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "salariesTeachers",
      label: "Salaries – Teachers",
      valueType: "currency",
      sources: [{ block: "Salaries", key: "Teachers" }],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "paraprofessionals",
      label: "Salaries – Paras",
      valueType: "currency",
      sources: [
        { block: "Salaries", key: "Paraprofessionals" },
        { block: "Salaries", key: "Paraprofessionals, P-T, Science" }, // Proposed variant
      ],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "inClassTutorsParas",
      label: "In Class Tutors / Paras",
      valueType: "currency",
      sources: [
        { block: "Salaries", key: "In Class Tutors / Paras" },
        { block: "Salaries", key: "In-Class Tutors / Paras" }, 
      ],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "summerSchool",
      label: "Summer School",
      valueType: "currency",
      sources: [
        { block: "Salaries", key: "Summer School" },
        { block: "Salaries", key: "Summer School (AE)" }, // Proposed variant
        { block: "Purchased Services", key: "Summer School" },
        { block: "Purchased Services", key: "Summer School (AE)" }, // just in case
      ],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "langArtsMathTutorsParas",
      label: "Lang Arts / Math Tutors / Paras",
      valueType: "currency",
      sources: [
        { block: "Salaries", key: "Lang Arts / Math Tutors / Paras" },
        { block: "Purchased Services", key: "Lang Arts / Math Tutors / Paras" },
      ],
      group: "salaries",
      defaultVisible: false,
    },

    // -----------------------------------------------------------
    // SERVICES & MATERIALS
    // -----------------------------------------------------------
    {
      key: "driverEarlyLiteracyProgram",
      label: "Driver Early Literacy Program",
      valueType: "currency",
      sources: [
        { block: "Salaries", key: "Driver Early Literacy Program" },
        { block: "Salaries", key: "Drivers / Early Literacy Program" }, // Proposed variant
        { block: "Purchased Services", key: "Driver Early Literacy Program" },
        {
          block: "Purchased Services",
          key: "Drivers / Early Literacy Program",
        }, // Proposed variant
      ],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "fieldTrips",
      label: "Field Trips",
      valueType: "currency",
      sources: [
        { block: "Salaries", key: "Field Trips" },
        { block: "Purchased Services", key: "Field Trips" },
        { block: "Supplies / Materials", key: "Field Trips" },
      ],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "exploringTheArts",
      label: "Exploring the Arts",
      valueType: "currency",
      sources: [{ block: "Purchased Services", key: "Exploring the Arts" }],
      group: "services",
      defaultVisible: false,
    },
    {
      key: "magnetSchools",
      label: "Magnet Schools",
      valueType: "currency",
      sources: [
        { block: "Purchased Services", key: "Magnet Schools" },
        { block: "Purchased Services", key: "Magnet School" }, // Proposed variant
      ],
      group: "services",
      defaultVisible: false,
    },

    // -----------------------------------------------------------
    // MATERIALS & OTHER
    // -----------------------------------------------------------
    {
      key: "instructionalMaterials",
      label: "Instructional Materials",
      valueType: "currency",
      sources: [
        { block: "Supplies / Materials", key: "Instructional Materials" },
      ],
      group: "materials",
      defaultVisible: false,
    },
    {
      key: "technologySoftware",
      label: "Technology / Software",
      valueType: "currency",
      sources: [
        { block: "Supplies / Materials", key: "Technology / Software" },
      ],
      group: "materials",
      defaultVisible: false,
    },
    {
      key: "duesFees",
      label: "Dues / Fees",
      valueType: "currency",
      sources: [{ block: "Other", key: "Dues / Fees" }],
      group: "materials",
      defaultVisible: false,
    },
  ],

  /**
   * Table configurations defining how the Approved and Actual
   * budget views are rendered in the UI.
   */
  tables: [
    {
      id: "approvedMain",
      title: "0300 BASIC EDUCATION – Approved",
      sectionKey: "Approved",
      fields: [
        "positionsAdmin",
        "positionsTeachers",
        "positionsTeachersTitleIGrant",

        "salariesAdmin",
        "salariesTeachers",
        "paraprofessionals",
        "inClassTutorsParas",
        "summerSchool",
        "langArtsMathTutorsParas",
        "driverEarlyLiteracyProgram",
        "fieldTrips",

        "exploringTheArts",
        "magnetSchools",

        "instructionalMaterials",
        "technologySoftware",
        "duesFees",
      ],
      columnStyles: {
        year: { width: "100px", minWidth: "100px" },

        positionsAdmin: { minWidth: "120px" },
        positionsTeachers: { minWidth: "100px" },
        positionsTeachersTitleIGrant: { minWidth: "100px" },

        salariesAdmin: { minWidth: "120px" },
        salariesTeachers: { minWidth: "110px" },
        paraprofessionals: { minWidth: "100px" },
        inClassTutorsParas: { minWidth: "120px" },
        summerSchool: { minWidth: "110px" },
        langArtsMathTutorsParas: { minWidth: "130px" },

        driverEarlyLiteracyProgram: { minWidth: "140px" },
        fieldTrips: { minWidth: "90px" },
        exploringTheArts: { minWidth: "120px" },
        magnetSchools: { minWidth: "110px" },
        instructionalMaterials: { minWidth: "130px" },
        technologySoftware: { minWidth: "130px" },
        duesFees: { minWidth: "90px" },
      },
    },

    {
      id: "actualMain",
      title: "0300 BASIC EDUCATION – Actual",
      sectionKey: "Actual",
      fields: [
        "positionsAdmin",
        "positionsTeachers",
        "positionsTeachersTitleIGrant",

        "salariesAdmin",
        "salariesTeachers",
        "paraprofessionals",
        "inClassTutorsParas",
        "summerSchool",
        "langArtsMathTutorsParas",
        "driverEarlyLiteracyProgram",
        "fieldTrips",

        "exploringTheArts",
        "magnetSchools",

        "instructionalMaterials",
        "technologySoftware",
        "duesFees",
      ],
      columnStyles: {
        year: { width: "100px", minWidth: "100px" },

        positionsAdmin: { minWidth: "120px" },
        positionsTeachers: { minWidth: "100px" },
        positionsTeachersTitleIGrant: { minWidth: "100px" },

        salariesAdmin: { minWidth: "120px" },
        salariesTeachers: { minWidth: "110px" },
        paraprofessionals: { minWidth: "100px" },
        inClassTutorsParas: { minWidth: "120px" },
        summerSchool: { minWidth: "110px" },
        langArtsMathTutorsParas: { minWidth: "130px" },

        driverEarlyLiteracyProgram: { minWidth: "140px" },
        fieldTrips: { minWidth: "90px" },
        exploringTheArts: { minWidth: "120px" },
        magnetSchools: { minWidth: "110px" },
        instructionalMaterials: { minWidth: "130px" },
        technologySoftware: { minWidth: "130px" },
        duesFees: { minWidth: "90px" },
      },
    },
  ],

  // No inflation aggregation for this program
  inflationSumFields: null,
};

/**
 * Program 0300 – BASIC EDUCATION
 *
 * Renders the Basic Education budget configuration into a <ProgramBudget /> component.
 * Provides:
 * - Automatic generation of Approved/Actual tables
 * - Editing support
 * - Mapped Firestore field extraction
 *
 * @component
 * @param {Object} props - Props forwarded to <ProgramBudget />.
 * @param {boolean} props.isEditing - Enables editing mode.
 * @param {Function} props.setBoeData - Callback used to collect edited values.
 * @returns {JSX.Element}
 */
export function Program0300(props) {
  return (
    <ProgramBudget programId="0300" config={PROGRAM_0300_CONFIG} {...props} />
  );
}
